import React, { useState, useEffect } from 'react';
import { apiCall } from '../config';
import './ShopifySalesData.css';

const ShopifySalesData = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Get current month and year
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Get first day of month and number of days
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const firstDayWeekday = firstDayOfMonth.getDay();

  // Generate calendar days
  const generateCalendarDays = () => {
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayWeekday; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dateString = date.toISOString().split('T')[0];
      days.push({
        day,
        date: dateString,
        hasData: calendarData[dateString] || false
      });
    }
    
    return days;
  };

  // Fetch calendar data for the current month
  useEffect(() => {
    fetchCalendarData();
  }, [currentMonth, currentYear]);

  const fetchCalendarData = async () => {
    try {
      const startOfMonth = new Date(currentYear, currentMonth, 1).toISOString().split('T')[0];
      const endOfMonth = new Date(currentYear, currentMonth + 1, 0).toISOString().split('T')[0];
      
      console.log('Fetching Shopify calendar data for:', startOfMonth, 'to', endOfMonth);
      
      const response = await apiCall(`/api/shopify-sales/calendar-data?start_date=${startOfMonth}&end_date=${endOfMonth}`);
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Shopify calendar data received:', data);
        setCalendarData(data);
      } else {
        const errorText = await response.text();
        console.error('API error:', errorText);
      }
    } catch (error) {
      console.error('Error fetching Shopify calendar data:', error);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const handleFetchData = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await apiCall('/api/shopify-sales/fetch-data', {
        method: 'POST',
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Fetch Shopify data response:', data);
        
        if (data.ready_for_import) {
          alert(`Sample data prepared successfully!\n\nTotal orders found: ${data.total_orders_found}\n\nSample data:\n${JSON.stringify(data.sample_data, null, 2)}`);
        } else {
          alert(data.message || 'Data fetched successfully');
        }
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error || 'Failed to fetch data'}`);
      }
    } catch (error) {
      console.error('Error fetching Shopify data:', error);
      alert('Error fetching data. Please check the console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="shopify-sales-data">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Shopify Sales Data Manager</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>View Shopify sales data with calendar visualization</li>
              <li>Fetch sales data by date range for analysis</li>
              <li>Track order patterns and sales performance</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        <div className="calendar-section">
        <div className="calendar-header">
          <button onClick={handlePreviousMonth} className="month-nav-btn">
            &lt; Previous
          </button>
          <h2>{monthNames[currentMonth]} {currentYear}</h2>
          <button onClick={handleNextMonth} className="month-nav-btn">
            Next &gt;
          </button>
        </div>

        <div className="calendar">
          <div className="calendar-weekdays">
            {weekdays.map(day => (
              <div key={day} className="weekday-header">{day}</div>
            ))}
          </div>
          
          <div className="calendar-days">
            {generateCalendarDays().map((dayData, index) => (
              <div 
                key={index} 
                className={`calendar-day ${!dayData ? 'empty' : ''} ${dayData?.hasData ? 'has-data' : ''}`}
              >
                {dayData && (
                  <>
                    <span className="day-number">{dayData.day}</span>
                    {dayData.hasData && <div className="data-indicator"></div>}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="calendar-legend">
          <div className="legend-item">
            <div className="legend-indicator has-data"></div>
            <span>Days with more than 2 orders</span>
          </div>
        </div>
      </div>

      <div className="data-fetch-section">
        <h3>Fetch Data by Date Range</h3>
        <div className="date-range-form">
          <div className="form-group">
            <label htmlFor="startDate">Start Date:</label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="endDate">End Date:</label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          
          <button 
            onClick={handleFetchData}
            disabled={isLoading || !startDate || !endDate}
            className="fetch-btn"
          >
            {isLoading ? 'Fetching...' : 'Fetch Data'}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ShopifySalesData; 